import {
  Chrome,
  Clock,
  Database,
  Instagram,
  Linkedin,
  MessageCircle,
  Search,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Target,
} from 'lucide-react-native';
import { StyleSheet, View } from 'react-native';
import { SkeletonCard } from '../../../components/animations/Skeleton';
import { Badge, Box, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { useAgentDisplayName } from '../hooks/useAgentDisplayName';
import { type AgentConfig, AgentType, type ModelTier } from '../types';
import { AgentSwitch, AgentToggleRow } from './AgentToggleRow';

interface AgentConfigCardProps {
  config?: AgentConfig;
  onToggleAgent: (agentType: string, enabled: boolean) => void;
  onConfigureAgent?: (agentType: AgentType) => void;
  isLoading: boolean;
}

const INTERNAL_AGENTS: Set<AgentType> = new Set([AgentType.FLOWSELLAI_COMPANY_RESEARCH]);

const AGENT_TYPE_ICONS: Array<{
  type: AgentType;
  icon: typeof Target;
  iconColor: string;
}> = [
  { type: AgentType.LEAD_QUALIFICATION, icon: Target, iconColor: colors.primary[500] },
  { type: AgentType.LEAD_ENRICHMENT, icon: Sparkles, iconColor: colors.accent.purple[400] },
  { type: AgentType.FOLLOW_UP_SCHEDULER, icon: Clock, iconColor: colors.status.warning.main },
  { type: AgentType.SENTIMENT_ANALYSIS, icon: MessageCircle, iconColor: colors.accent.blue[400] },
  { type: AgentType.PROMPT_ENRICHER, icon: Search, iconColor: colors.primary[300] },
  { type: AgentType.LINKEDIN_COMPANY_RESEARCH, icon: Linkedin, iconColor: colors.accent.blue[500] },
  { type: AgentType.BROWSER_COMPANY_RESEARCH, icon: Chrome, iconColor: colors.accent.amber[500] },
  { type: AgentType.FLOWSELLAI_COMPANY_RESEARCH, icon: Database, iconColor: colors.primary[400] },
  {
    type: AgentType.INSTAGRAM_COMPANY_RESEARCH,
    icon: Instagram,
    iconColor: colors.accent.purple[500],
  },
].filter((a) => !INTERNAL_AGENTS.has(a.type));

export function AgentConfigCard({
  config,
  onToggleAgent,
  onConfigureAgent,
  isLoading,
}: Readonly<AgentConfigCardProps>) {
  const { t } = useTranslation();
  const agentName = useAgentDisplayName();

  if (isLoading) {
    return <SkeletonCard style={styles.skeleton} />;
  }

  if (!config) return null;

  const tierConfig: Record<ModelTier, { label: string; color: 'green' | 'amber' | 'purple' }> = {
    fast: { label: t('agents.config.tiers.fast'), color: 'green' },
    balanced: { label: t('agents.config.tiers.balanced'), color: 'amber' },
    premium: { label: t('agents.config.tiers.premium'), color: 'purple' },
  };

  const tier = tierConfig[config.modelTier];

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <HStack spacing="sm" align="center" justify="between">
          <HStack spacing="sm" align="center">
            <Settings size={16} color={colors.primary[400]} strokeWidth={2} />
            <Text variant="h5" color={colors.text.primary} style={styles.title}>
              {t('agents.config.title')}
            </Text>
          </HStack>
          <Badge color={tier.color} variant="subtle" size="sm">
            {tier.label}
          </Badge>
        </HStack>
      </View>

      <View style={styles.body}>
        <VStack spacing="xs">
          {AGENT_TYPE_ICONS.map((agent) => {
            const enabled = config.enabledAgents.includes(agent.type);
            return (
              <AgentToggleRow
                key={agent.type}
                label={agentName(agent.type)}
                icon={agent.icon}
                iconColor={agent.iconColor}
                enabled={enabled}
                onPress={() => onToggleAgent(agent.type, !enabled)}
                onLabel={t('common.states.on')}
                offLabel={t('common.states.off')}
                rightContent={
                  <HStack spacing="xs" align="center" style={styles.rightActions}>
                    {onConfigureAgent && (
                      <Box
                        pressable
                        onPress={() => onConfigureAgent(agent.type)}
                        borderRadius="full"
                        alignItems="center"
                        justifyContent="center"
                        style={styles.configureButton}
                      >
                        <SlidersHorizontal size={12} color={colors.text.tertiary} strokeWidth={2} />
                      </Box>
                    )}
                    <AgentSwitch
                      enabled={enabled}
                      onLabel={t('common.states.on')}
                      offLabel={t('common.states.off')}
                    />
                  </HStack>
                }
              />
            );
          })}
        </VStack>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  rightActions: {
    flexShrink: 0,
  },
  configureButton: {
    width: 24,
    height: 24,
    backgroundColor: colors.background.tertiary,
  },
  skeleton: {
    minHeight: 220,
  },
  card: {
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.default,
    overflow: 'hidden',
  },
  header: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.md,
  },
  title: {
    fontWeight: '700',
    letterSpacing: -0.3,
  },
  body: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.md,
  },
});
